/* -----------------Globals--------------- */
import { useCallback } from "react";

/* -----------------Constants--------------- */
import { normalizeProethicaSessionResponse } from "@constants/demo";
import { events } from "@constants/tracking";
/* -----------------Helpers & Hooks--------------- */
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { flattenSessionItems } from "@helpers/session";
import { trackCustomEvent } from "analytics";
/* -----------------Router--------------- */
import { useRouter } from "expo-router";
import { fetchSessionItems } from "../api/mutations";
import useSessionStore from "./useSessionStore";

export type ResumeOutcome = "resumed" | "report" | "failed";

export const useResumeSession = () => {
	const router = useRouter();
	const updateSession = useSessionStore((state) => state.updateSession);
	const updateCurrentItemIndex = useSessionStore(
		(state) => state.updateCurrentItemIndex,
	);

	const handleResumeSession = useCallback(
		async (numericId: number): Promise<ResumeOutcome> => {
			try {
				const response = normalizeProethicaSessionResponse(
					await fetchSessionItems({ sessionId: numericId }),
				);

				if (response.status === "COMPLETE") {
					router.replace(`/session/${numericId}/report/`);
					return "report";
				}

				if (!response.sessionItems?.length) {
					return "failed";
				}

				trackCustomEvent({ eventName: events.sessionResumed, numericId });

				updateSession({
					sessionResponse: {
						...response,
						sessionItems: flattenSessionItems(response.sessionItems),
					},
					isSummativeTest: false,
				});
				updateCurrentItemIndex(0);
				router.push({
					pathname: "/(protected)/session/[id]",
					params: { id: String(response.numericId) },
				});
				return "resumed";
			} catch (error) {
				captureErrorToSentry(new Error("Error resuming session"), {
					error: JSON.stringify(error),
				});
				return "failed";
			}
		},
		[router, updateCurrentItemIndex, updateSession],
	);

	return {
		handleResumeSession,
	};
};
